import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { loadAdminDocuments } from './adminDocuments';

const buttonStyle = {
  background: '#1ecfff',
  color: '#181a2a',
  border: 'none',
  borderRadius: 8,
  padding: '0.5rem 1.2rem',
  fontSize: '1rem',
  fontWeight: 600,
  cursor: 'pointer',
  boxShadow: '0 2px 8px rgba(30,207,255,0.10)'
};

function formatBytes(bytes) {
  const size = Number(bytes);
  if (!Number.isFinite(size) || size <= 0) return '0 B';
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(2)} MB`;
}

function dataUrlToBlobUrl(dataUrl) {
  if (typeof dataUrl !== 'string' || !dataUrl.startsWith('data:')) {
    return { url: dataUrl, revoke: null };
  }

  const splitIndex = dataUrl.indexOf(',');
  if (splitIndex === -1) {
    return { url: dataUrl, revoke: null };
  }

  const metadata = dataUrl.slice(5, splitIndex);
  const payload = dataUrl.slice(splitIndex + 1);
  const mimeType = (metadata.split(';')[0] || 'application/octet-stream').trim() || 'application/octet-stream';
  const isBase64 = metadata.includes(';base64');

  const binaryString = isBase64 ? atob(payload) : decodeURIComponent(payload);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }

  const blobUrl = URL.createObjectURL(new Blob([bytes], { type: mimeType }));
  return { url: blobUrl, revoke: () => URL.revokeObjectURL(blobUrl) };
}

export default function DocumentsPage() {
  const navigate = useNavigate();
  const documents = useMemo(() => loadAdminDocuments(), []);

  function openDocument(doc) {
    const newWindow = window.open('', '_blank');
    if (!newWindow) return null;

    try {
      const { url, revoke } = dataUrlToBlobUrl(doc.dataUrl);
      newWindow.location.replace(url);
      if (revoke) {
        setTimeout(revoke, 60_000);
      }
      return newWindow;
    } catch (err) {
      newWindow.location.replace(doc.dataUrl);
      return newWindow;
    }
  }

  function handlePrintDocument(doc) {
    const printWindow = openDocument(doc);
    if (!printWindow) {
      return;
    }

    printWindow.focus();
    setTimeout(() => {
      try {
        printWindow.print();
      } catch (err) {
        // Fallback: user can print from browser controls.
      }
    }, 500);
  }

  return (
    <div style={{ maxWidth: 760, margin: '3rem auto', background: '#181a2a', borderRadius: 16, padding: '2.5rem 2rem', color: 'white', boxShadow: '0 4px 32px rgba(0,0,0,0.18)' }}>
      <h2 style={{ color: '#1ecfff', fontWeight: 700, fontSize: '2rem', marginBottom: '1rem' }}>
        Documents
      </h2>
      <button
        onClick={() => navigate('/dashboard')}
        style={{ ...buttonStyle, marginBottom: '1.3rem', background: '#b6eaff', color: '#181a2a' }}
      >
        Back to Main Page
      </button>

      <div style={{ color: '#8db7e6', marginBottom: 12, fontSize: '0.94rem' }}>
        Shared operational documents uploaded by admin.
      </div>

      {documents.length === 0 ? (
        <div style={{ color: '#b6eaff' }}>No documents available yet.</div>
      ) : (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {documents.map(doc => (
            <li
              key={doc.id}
              style={{
                background: '#1a2338',
                border: '1px solid #35507f',
                borderRadius: 8,
                padding: '0.8rem',
                marginBottom: 8,
                display: 'flex',
                justifyContent: 'space-between',
                gap: 10,
                alignItems: 'center',
                flexWrap: 'wrap',
              }}
            >
              <div style={{ color: '#d7ebff' }}>
                <div style={{ fontWeight: 700 }}>
                  <span>{doc.name}</span>
                </div>
                <div style={{ color: '#8db7e6', fontSize: '0.9rem' }}>
                  {formatBytes(doc.size)} • {new Date(doc.uploadedAt).toLocaleString()}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  type="button"
                  onClick={() => openDocument(doc)}
                  style={{
                    background: '#9ed0ff',
                    color: '#10233a',
                    border: 'none',
                    borderRadius: 8,
                    padding: '0.45rem 0.8rem',
                    fontWeight: 700,
                    cursor: 'pointer',
                  }}
                >
                  Open
                </button>
                <a
                  href={doc.dataUrl}
                  download={doc.name}
                  style={{
                    textDecoration: 'none',
                    background: '#1ecfff',
                    color: '#090a12',
                    borderRadius: 8,
                    padding: '0.45rem 0.8rem',
                    fontWeight: 700,
                  }}
                >
                  Download
                </a>
                <button
                  type="button"
                  onClick={() => handlePrintDocument(doc)}
                  style={{
                    background: '#ffd27a',
                    color: '#2e1a00',
                    border: 'none',
                    borderRadius: 8,
                    padding: '0.45rem 0.8rem',
                    fontWeight: 700,
                    cursor: 'pointer',
                  }}
                >
                  Print
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
